"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SellerClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const errors_1 = require("@deliverr/errors");
const SettingsExceptions_1 = require("./SettingsExceptions");
const defaultConfig = {
    baseUrl: process.env.SELLER_SERVICE_URL,
    user: process.env.SELLER_BASIC_AUTH_USER,
    password: process.env.SELLER_BASIC_AUTH_PASSWORD,
};
class SellerClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = defaultConfig) {
        super({
            baseURL: `${config.baseUrl}/v1`,
            basicAuthUser: config.user,
            basicAuthPassword: config.password,
        });
        if (!config.user || !config.password) {
            commons_logs_1.log.warn("SELLER_BASIC_AUTH_USER and SELLER_BASIC_AUTH_PASSWORD environment variable is not set");
        }
    }
    async getStrategicSellerIds() {
        try {
            const { sellerIds } = await this.getCached("/admin/strategic-seller-ids");
            return sellerIds;
        }
        catch (error) {
            this.handleSellerError("getStrategicSellerIds", error);
            return [];
        }
    }
    async isStrategicSellerId(sellerId) {
        return (await this.getStrategicSellerIds()).indexOf(sellerId) >= 0;
    }
    async getSettings(sellerId) {
        try {
            return await this.get(`/settings/${sellerId}`);
        }
        catch (error) {
            this.handleSellerError("getSettings", error, { sellerId });
        }
    }
    async getAdminSettings() {
        try {
            return await this.get(`/admin-settings`);
        }
        catch (error) {
            this.handleSellerError("getAdminSettings", error);
        }
    }
    async createSettings(sellerId) {
        try {
            return await this.post(`/settings/${sellerId}`, null);
        }
        catch (error) {
            this.handleSellerError("createSettings", error, { sellerId });
        }
    }
    async createOrUpdateSettings(sellerId, settings) {
        try {
            await this.put(`/settings/${sellerId}`, settings);
        }
        catch (error) {
            this.handleSellerError("createOrUpdateSettings", error, { sellerId });
        }
    }
    async getSellerBySellerId(sellerId) {
        try {
            return await this.get("/seller", { sellerId });
        }
        catch (error) {
            this.handleSellerServiceError("getSellerBySellerId", error, { sellerId });
        }
    }
    async isCompanyTaken(email) {
        try {
            return await this.get("/seller/exists", { email });
        }
        catch (error) {
            this.handleSellerServiceError("isCompanyTaken", error, { email });
        }
    }
    async createSeller(createdBy, email) {
        try {
            return await this.post("/seller", {
                createdBy,
                email,
            });
        }
        catch (error) {
            this.handleSellerServiceError("createSeller", error);
        }
    }
    async addSellerTags(sellerId, tag) {
        try {
            return await this.put(`/seller/${sellerId}/tags/${tag}`, null);
        }
        catch (error) {
            this.handleSellerServiceError("addSellerTags", error);
        }
    }
    async updateEmailClaimOfSeller(sellerId, email) {
        try {
            return await this.put(`/seller/${sellerId}/update`, null, {
                email,
            });
        }
        catch (error) {
            this.handleSellerServiceError("updateEmailClaimOfSeller", error);
        }
    }
    async updateIsCatalogImportedClaimOfSeller(sellerId, isCatalogImported) {
        try {
            return this.put(`/seller/${sellerId}/update`, null, {
                isCatalogImported,
            });
        }
        catch (error) {
            this.handleSellerServiceError("updateIsCatalogImportedClaimOfSeller", error);
        }
    }
    async assignSellerIdentityToAdminAccount(sellerId) {
        try {
            return this.put(`seller/${sellerId}/ghost`, null);
        }
        catch (error) {
            this.handleSellerServiceError("assignSellerIdentityToAdminAccount", error);
        }
    }
    async makeDeliverrEmployeeAdmin(sellerId) {
        try {
            return this.put(`/admin/seller/${sellerId}/add-admin`, null);
        }
        catch (err) {
            this.handleSellerServiceError("makeDeliverrEmployeeAdmin", err);
        }
    }
    handleSellerError(functionName, err, logData) {
        const ctx = Object.assign({ functionName }, logData);
        if (!err.response) {
            const unknownError = new errors_1.DeliverrError({
                code: errors_1.ApiError.UNKNOWN_ERROR,
                functionName,
                payload: err,
            });
            commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err }));
            throw unknownError;
        }
        switch (err.response.status) {
            case 400:
                const formatError = new errors_1.DeliverrError({
                    code: SettingsExceptions_1.SettingsExceptions.INVALID_SETTINGS,
                    functionName,
                });
                commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err: formatError }));
                throw formatError;
            case 403:
                const unauthorizedErr = new errors_1.DeliverrError({
                    code: SettingsExceptions_1.SettingsExceptions.UNAUTHORIZED_ACCESS,
                    functionName,
                });
                commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err: unauthorizedErr }));
                throw unauthorizedErr;
            case 504:
                const networkError = new errors_1.DeliverrError({
                    code: errors_1.ApiError.NETWORK_ERROR,
                    functionName,
                });
                commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err: networkError }));
                throw networkError;
            default:
                const unknownError = new errors_1.DeliverrError({
                    code: errors_1.ApiError.UNKNOWN_ERROR,
                    functionName,
                    payload: err,
                });
                commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err }));
                throw unknownError;
        }
    }
    handleSellerServiceError(functionName, err, logData) {
        const ctx = Object.assign({ functionName }, logData);
        if (!err.response) {
            const unknownError = new errors_1.DeliverrError({
                code: errors_1.ApiError.UNKNOWN_ERROR,
                functionName,
                payload: err,
            });
            commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err }));
            throw unknownError;
        }
        if (err.response.data.subcode) {
            const reportableError = new errors_1.DeliverrError({
                code: err.response.data.subcode,
                functionName,
            });
            commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err: reportableError }));
            throw reportableError;
        }
        else {
            const unknownError = new errors_1.DeliverrError({
                code: err.response.data.subcode,
                functionName,
                payload: err,
            });
            commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { err }));
            throw unknownError;
        }
    }
}
exports.SellerClient = SellerClient;
//# sourceMappingURL=SellerClient.js.map